import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { playSoundEffect } from '../../../hooks/useSoundEffects';
import { getExploreUnknownMinigameWinRewardImageSrc } from '../../../config/exploreUnknownMinigameWinRewardImages';
import {
  BALLOON_BOOTH_BACKGROUND_SRC,
  BALLOON_BOOTH_SQUEAK_SOUND_SRC,
  BALLOON_BOOTH_POP_SOUND_SRC,
  BALLOON_BOOTH_GAS_IMAGE_SRC,
  BALLOON_BOOTH_GAS_SOUND_SRC,
  BALLOON_BOOTH_POP_EFFECT_MS,
  BALLOON_BOOTH_GAS_FADE_MS,
  getBalloonBoothBalloonImageSrc,
  balloonBoothLogicalToPercentPosition,
  balloonBoothLogicalToEffectPercentPosition,
} from './balloonBoothPopLayout';
import './BalloonBoothPopMinigame.css';

/**
 * @param {object} props
 * @param {{ balloonCount: number, maxPops: number, completionPickValue: number, balloons: { index: number, color: string, x: number, y: number, outcome: string, rewardId: string | null }[] }} props.challenge
 * @param {(pickedTreeIndex: number, opts?: { poppedBalloonIndices?: number[] }) => void} props.onPickTree
 * @param {boolean} props.disabled
 * @param {React.RefObject<HTMLElement>} props.containerRef
 * @param {null | 'won' | 'lost'} props.resultOutcome
 * @param {(() => void) | undefined} props.onResultProceed
 * @param {{ id: string } | null} props.winReward
 * @param {Array<{ id: string }> | null} props.winRewards
 */
export default function BalloonBoothPopMinigame({
  challenge,
  onPickTree,
  disabled = false,
  containerRef,
  resultOutcome = null,
  onResultProceed,
  winReward = null,
  winRewards = null,
}) {
  const { t } = useTranslation();
  const playArenaRef = useRef(null);
  const submittedRef = useRef(false);
  const popOrderRef = useRef([]);
  const effectTimersRef = useRef([]);

  const maxPops = challenge.maxPops;
  const completionPickValue = challenge.completionPickValue;
  const [popsRemaining, setPopsRemaining] = useState(maxPops);
  const [poppedIndices, setPoppedIndices] = useState(() => new Set());
  const [poppingIndex, setPoppingIndex] = useState(null);
  const [resolvingPop, setResolvingPop] = useState(false);
  const [effects, setEffects] = useState([]);

  const resolvedWinRewards =
    Array.isArray(winRewards) && winRewards.length > 0
      ? winRewards
      : winReward && typeof winReward.id === 'string'
        ? [winReward]
        : [];

  const setPlayArenaRef = useCallback(
    (node) => {
      playArenaRef.current = node;
      if (containerRef && typeof containerRef === 'object') {
        containerRef.current = node;
      }
    },
    [containerRef]
  );

  const clearEffectTimers = useCallback(() => {
    for (const id of effectTimersRef.current) {
      window.clearTimeout(id);
    }
    effectTimersRef.current = [];
  }, []);

  useEffect(() => () => clearEffectTimers(), [clearEffectTimers]);

  const finishSession = useCallback(() => {
    if (submittedRef.current || disabled || resultOutcome != null) {
      return;
    }
    submittedRef.current = true;
    onPickTree(completionPickValue, { poppedBalloonIndices: [...popOrderRef.current] });
  }, [completionPickValue, disabled, onPickTree, resultOutcome]);

  const handleBalloonActivate = useCallback(
    (balloonIndex) => {
      if (
        submittedRef.current ||
        disabled ||
        resultOutcome != null ||
        resolvingPop ||
        popsRemaining < 1 ||
        poppedIndices.has(balloonIndex)
      ) {
        return;
      }
      const balloon = challenge.balloons.find((b) => b.index === balloonIndex);
      if (!balloon) {
        throw new Error(`BalloonBoothPopMinigame: unknown balloon index ${balloonIndex}`);
      }

      setResolvingPop(true);
      setPoppingIndex(balloonIndex);
      playSoundEffect(BALLOON_BOOTH_POP_SOUND_SRC);

      const effectPos = balloonBoothLogicalToEffectPercentPosition(balloon.x, balloon.y);
      const effectId = `pop-${balloonIndex}-${Date.now()}`;
      const isGas = balloon.outcome === 'gas';

      if (isGas) {
        playSoundEffect(BALLOON_BOOTH_GAS_SOUND_SRC);
        setEffects((prev) => [
          ...prev,
          {
            id: effectId,
            type: 'gas',
            left: effectPos.left,
            top: effectPos.top,
            visible: false,
          },
        ]);
        effectTimersRef.current.push(
          window.setTimeout(() => {
            setEffects((prev) =>
              prev.map((e) => (e.id === effectId ? { ...e, visible: true } : e))
            );
          }, 32)
        );
      } else if (typeof balloon.rewardId === 'string') {
        const rewardSrc = getExploreUnknownMinigameWinRewardImageSrc(balloon.rewardId);
        setEffects((prev) => [
          ...prev,
          {
            id: effectId,
            type: 'reward',
            left: effectPos.left,
            top: effectPos.top,
            rewardSrc,
            rewardId: balloon.rewardId,
            visible: false,
          },
        ]);
        effectTimersRef.current.push(
          window.setTimeout(() => {
            setEffects((prev) =>
              prev.map((e) => (e.id === effectId ? { ...e, visible: true } : e))
            );
          }, 32)
        );
      }

      const settleMs = isGas ? BALLOON_BOOTH_GAS_FADE_MS : BALLOON_BOOTH_POP_EFFECT_MS;
      effectTimersRef.current.push(
        window.setTimeout(() => {
          setPoppedIndices((prev) => {
            const next = new Set(prev);
            next.add(balloonIndex);
            return next;
          });
          setPoppingIndex(null);
          popOrderRef.current.push(balloonIndex);
          setPopsRemaining((prev) => {
            const next = prev - 1;
            setResolvingPop(false);
            if (next <= 0) {
              window.setTimeout(() => finishSession(), 350);
            }
            return next;
          });
          effectTimersRef.current.push(
            window.setTimeout(() => {
              setEffects((prev) => prev.filter((e) => e.id !== effectId));
            }, 400)
          );
        }, settleMs)
      );
    },
    [
      challenge.balloons,
      disabled,
      finishSession,
      poppedIndices,
      popsRemaining,
      resolvingPop,
      resultOutcome,
    ]
  );

  const handleBalloonHover = useCallback(
    (balloonIndex) => {
      if (poppedIndices.has(balloonIndex) || resolvingPop) {
        return;
      }
      playSoundEffect(BALLOON_BOOTH_SQUEAK_SOUND_SRC);
    },
    [poppedIndices, resolvingPop]
  );

  return (
    <div className="balloon-booth">
      <div
        ref={setPlayArenaRef}
        className="balloon-booth-play-arena"
        role="application"
        aria-label={t('exploreUnknownPage.minigame.balloonBooth.playArenaAria')}
      >
        <div className="balloon-booth-bg-wrap">
          <img
            className="balloon-booth-bg"
            src={BALLOON_BOOTH_BACKGROUND_SRC}
            alt=""
            draggable={false}
          />
        </div>
        <div className="balloon-booth-hud balloon-booth-hud--top" data-no-minigame-flashlight="true">
          <h3 className="balloon-booth-game-title">{t('exploreUnknownPage.minigame.balloonBooth.title')}</h3>
          {resultOutcome == null ? (
            <>
              <p className="balloon-booth-prompt">{t('exploreUnknownPage.minigame.balloonBooth.prompt')}</p>
              <p className="balloon-booth-pops-left">
                {t('exploreUnknownPage.minigame.balloonBooth.popsRemaining', { count: popsRemaining })}
              </p>
            </>
          ) : null}
        </div>
        {resultOutcome == null ? (
        <div className="balloon-booth-balloons balloon-booth-balloons--active">
          {challenge.balloons.map((balloon) => {
            const pos = balloonBoothLogicalToPercentPosition(balloon.x, balloon.y);
            const isPopped = poppedIndices.has(balloon.index);
            const isPopping = poppingIndex === balloon.index;
            return (
              <button
                key={`balloon-${balloon.index}`}
                type="button"
                className={`balloon-booth-balloon${isPopping ? ' balloon-booth-balloon--popping' : ''}`}
                style={{ left: pos.left, top: pos.top }}
                disabled={
                  disabled ||
                  resultOutcome != null ||
                  resolvingPop ||
                  isPopped ||
                  popsRemaining < 1
                }
                onMouseEnter={() => handleBalloonHover(balloon.index)}
                onFocus={() => handleBalloonHover(balloon.index)}
                onClick={() => handleBalloonActivate(balloon.index)}
                aria-label={t('exploreUnknownPage.minigame.balloonBooth.balloonAria', {
                  index: balloon.index + 1,
                })}
              >
                {!isPopped ? (
                  <img
                    className="balloon-booth-balloon-img"
                    src={getBalloonBoothBalloonImageSrc(balloon.color)}
                    alt=""
                    draggable={false}
                  />
                ) : null}
              </button>
            );
          })}
        </div>
        ) : null}
        <div className="balloon-booth-effects" aria-hidden="true">
          {effects.map((effect) =>
            effect.type === 'gas' ? (
              <span
                key={effect.id}
                className="balloon-booth-effect"
                style={{ left: effect.left, top: effect.top }}
              >
                <img
                  className={`balloon-booth-gas-img${effect.visible ? ' balloon-booth-gas-img--visible' : ''}`}
                  src={BALLOON_BOOTH_GAS_IMAGE_SRC}
                  alt=""
                  draggable={false}
                />
              </span>
            ) : (
              <span
                key={effect.id}
                className="balloon-booth-effect"
                style={{ left: effect.left, top: effect.top }}
              >
                <img
                  className={`balloon-booth-reward-img${effect.visible ? ' balloon-booth-reward-img--visible' : ''}`}
                  src={effect.rewardSrc}
                  alt=""
                  draggable={false}
                />
              </span>
            )
          )}
        </div>
        {resultOutcome != null ? (
          <div
            className="balloon-booth-hud balloon-booth-hud--bottom balloon-booth-result-banner balloon-booth-result-banner--won"
            role="status"
            aria-live="polite"
            data-no-minigame-flashlight="true"
          >
            <p className="balloon-booth-result-banner-title">
              {t('exploreUnknownPage.minigame.balloonBooth.successTitle')}
            </p>
            {resolvedWinRewards.length > 0 ? (
              <div className="balloon-booth-result-rewards">
                {resolvedWinRewards.map((reward, idx) => {
                  const bodyKey = `exploreUnknownPage.minigame.balloonBooth.winReward.${reward.id}`;
                  return (
                    <p key={`${reward.id}-${idx}`} className="balloon-booth-result-banner-body">
                      {t(bodyKey, {
                        percent: reward.darknessReductionApplied,
                        xp: reward.xpGained,
                        health: reward.startHealthBonus != null ? reward.startHealthBonus : 3,
                        billyBucks: reward.billyBucksAmount,
                        defaultValue: reward.id,
                      })}
                    </p>
                  );
                })}
              </div>
            ) : (
              <p className="balloon-booth-result-banner-body">
                {t('exploreUnknownPage.minigame.balloonBooth.successBodyGeneric')}
              </p>
            )}
            {typeof onResultProceed === 'function' ? (
              <button
                type="button"
                className="balloon-booth-result-proceed"
                onClick={() => onResultProceed()}
              >
                {t('exploreUnknownPage.minigame.proceed')}
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
